/**
 * Sefer modülünün açılır listeleri. İlk dördü müşterinin `ISM_Voyage_Reporting_Structure.xlsx`
 * "Dropdown Listeleri" sayfasıyla birebir aynıdır (`docs/gereksinimler/sefer-raporlama.md`).
 * Değerler kod; ekrandaki adı çeviri dosyasından gelir.
 */

export const VOYAGE_STATUSES = ['planned', 'active', 'completed', 'cancelled'] as const;
export type VoyageStatus = (typeof VOYAGE_STATUSES)[number];

export const COMMERCIAL_STATUSES = ['on_hire', 'off_hire', 'waiting_for_charter', 'open'] as const;
export type CommercialStatus = (typeof COMMERCIAL_STATUSES)[number];

export const OPERATIONAL_STATUSES = [
  'in_port',
  'sailing',
  'at_anchor',
  'drifting',
  'waiting',
  'strait_transit',
  'canal_transit',
  'pilotage',
  'cargo_operation',
] as const;
export type OperationalStatus = (typeof OPERATIONAL_STATUSES)[number];

/** Demirleme, bekleme ve sürüklenme nedeni ("Berth/weather/orders vb."). */
export const WAITING_REASONS = [
  'waiting_for_charter',
  'waiting_for_berth',
  'waiting_for_orders',
  'weather',
  'traffic',
  'port_congestion',
  'other',
] as const;
export type WaitingReason = (typeof WAITING_REASONS)[number];

/**
 * Yakıt türleri: Noon Report ekranındakiler, yeni sefer ekranındaki VLSFO (ISO 8217 adları) ve
 * ortağın ROB tablosundaki LSFO (ADR 0057 §3).
 */
export const FUEL_TYPES = ['HFO', 'VLSFO', 'LSFO', 'ULSFO', 'MDO', 'MGO', 'LSMGO', 'LNG'] as const;
export type FuelType = (typeof FUEL_TYPES)[number];

export const LOAD_CONDITIONS = ['laden', 'ballast'] as const;
export type LoadCondition = (typeof LOAD_CONDITIONS)[number];

export const PROPELLER_TYPES = ['fixed', 'controllable'] as const;
export const MACHINERY_STATUSES = ['running', 'standby', 'stopped', 'not_in_use'] as const;
export const CURRENT_RELATIONS = ['head', 'following', 'beam'] as const;
export const CARGO_OPERATIONS = ['loading', 'discharging'] as const;

/**
 * Yağ türleri (ortağın ROB tablosu; ADR 0057 §3): ana makine silindir ve sistem yağı, yardımcı
 * makine yağı, şaft (stern tube), hidrolik, diğer. Metrik ton (ortağın cevabı #3).
 */
export const LUBE_TYPES = [
  'ME_CYL',
  'ME_SYS',
  'AE_LO',
  'STERN_TUBE',
  'HYDRAULIC',
  'OTHER',
] as const;
export type LubeType = (typeof LUBE_TYPES)[number];

/** Rüzgâr yönü: 16 pusula yönü (ortağın "NE / 3 Bft" gösterimi; ADR 0057 §3). */
export const COMPASS_POINTS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
] as const;
export type CompassPoint = (typeof COMPASS_POINTS)[number];

/**
 * Görüş: Met Office deniz tahmini terimleri (very poor < 1000 m, poor 1000 m – 2 nm, moderate
 * 2–5 nm, good > 5 nm; ADR 0057 §3).
 */
export const VISIBILITY_GRADES = ['very_poor', 'poor', 'moderate', 'good'] as const;
export type VisibilityGrade = (typeof VISIBILITY_GRADES)[number];

/** Gemi ve donanım durumu (Hull, Machinery, Navigation, Safety; arrival-report.jpeg). */
export const CONDITION_GRADES = ['satisfactory', 'unsatisfactory', 'not_checked'] as const;
export type ConditionGrade = (typeof CONDITION_GRADES)[number];

/** Klas ve statutory sertifikaların durumu (arrival-report.jpeg "Valid…"). */
export const CERTIFICATE_GRADES = ['valid', 'expiring', 'expired', 'not_checked'] as const;
export type CertificateGrade = (typeof CERTIFICATE_GRADES)[number];

/**
 * Sonraki işlem (Next Operation; g5.jpeg, ortağın cevabı #6; ADR 0057 §5): sea passage (COSP ya
 * da seyrin devamı), yanaşma, demirleme, sürüklenme, boğaz/kanal geçişi, emir bekleme, kalkış,
 * limanda kalış.
 */
export const NEXT_OPERATIONS = [
  'sea_passage',
  'berth',
  'anchorage',
  'drifting',
  'strait_canal',
  'waiting_orders',
  'departure',
  // Limanda kal: sıradaki rapor Port Daily (ortak geri bildirimi 2026-10-19; ADR 0083 Ek)
  'port_stay',
] as const;
export type NextOperation = (typeof NEXT_OPERATIONS)[number];

/* ---------- Kalkış ve varış (ADR 0058 §2, §6) ---------- */

/**
 * Limandaki yük işlemi (tel kafes "Loaded / Discharged / Both / None"): Departure'da yapılan,
 * Berthing'de planlanan işlem.
 */
export const CARGO_PORT_OPERATIONS = ['loading', 'discharging', 'both', 'none'] as const;
export type CargoPortOperation = (typeof CARGO_PORT_OPERATIONS)[number];

/** Yük birimi; miktar ve birim ayrı saklanır (tel kafes "MT / m³ / TEU / units"). */
export const CARGO_UNITS = ['MT', 'M3', 'TEU', 'UNITS'] as const;
export type CargoUnit = (typeof CARGO_UNITS)[number];

/** Kalkıştaki istihdam (tel kafes "Employment / commercial status"). */
export const EMPLOYMENT_STATUSES = [
  'chartered',
  'unemployed',
  'owners_operation',
  'maintenance',
] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

/** Kalkış amacı (tel kafes "Departure purpose"). */
export const DEPARTURE_PURPOSES = [
  'cargo_voyage',
  'repositioning',
  'sea_trial',
  'bunker',
  'other',
] as const;
export type DeparturePurpose = (typeof DEPARTURE_PURPOSES)[number];

/** AB rota taraması (tel kafes; nihai MRV kararı değildir, ADR 0058 §2). */
export const EU_ROUTES = ['eu_eu', 'eu_non_eu', 'non_eu_eu', 'non_eu_non_eu', 'unknown'] as const;
export type EuRoute = (typeof EU_ROUTES)[number];

/** MRV inceleme durumu: türetilir, saklanmaz; "assessed" MRV modülüyle (Faz 3). */
export const MRV_REVIEW_STATUSES = ['not_assessed', 'review_required', 'assessed'] as const;
export type MrvReviewStatus = (typeof MRV_REVIEW_STATUSES)[number];

/** Sefer kararı (tel kafes "Link existing / Create new / No voyage yet"; ADR 0058 §3). */
export const VOYAGE_ACTIONS = ['link', 'create', 'none'] as const;
export type VoyageAction = (typeof VOYAGE_ACTIONS)[number];

/** Kalkış kontrol listesinin özeti (tel kafes "Complete / Incomplete / N/A"). */
export const CHECKLIST_STATUSES = ['complete', 'incomplete', 'not_applicable'] as const;
export type ChecklistStatus = (typeof CHECKLIST_STATUSES)[number];

/** Yanaşma tarafı (arrival-report.jpeg "Port Side / Starboard Side"). */
export const BERTH_SIDES = ['port', 'starboard'] as const;
export type BerthSide = (typeof BERTH_SIDES)[number];

/* ---------- Seferin günlük raporları (ADR 0059 §3) ---------- */

/**
 * Günlük raporda konum durumu / faaliyet (`g1.jpeg` "Activity Status"): rıhtımda, yük işleminde,
 * ikmalde, onarımda, beklemede, demirde, sürüklenmede, diğer.
 */
export const DAILY_ACTIVITIES = [
  'alongside',
  'cargo_operation',
  'bunkering',
  'repairs',
  'waiting',
  'at_anchor',
  'drifting',
  'other',
] as const;
export type DailyActivity = (typeof DAILY_ACTIVITIES)[number];
